import { useApi } from '@hooks/api.hook';
import { useGetCurrentUser } from '@queries/users.query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SubsystemCreateDto, SubsystemSelectDto } from '../types/api.types';
import { queryKeys } from './keys';

export const useCreateTechnologySubsystem = () => {
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: SubsystemSelectDto, variables: SubsystemCreateDto) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnology(variables.technologyId),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologies });
  };

  return useMutation({
    mutationFn: async (data: SubsystemCreateDto) => {
      if (!isInnovator) {
        throw new Error('Only innovators can create subsystems');
      }
      return await apiPost<SubsystemSelectDto>({
        path: '/v1/innovator/subsystems',
        body: data,
      });
    },
    onSuccess,
  });
};

export const useUpdateTechnologySubsystem = () => {
  const { apiPatch } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_data: SubsystemSelectDto, variables: SubsystemCreateDto & { id: string }) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnology(variables.technologyId),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologies });
  };

  return useMutation({
    mutationFn: async (data: SubsystemCreateDto & { id: string }) => {
      if (!isInnovator) {
        throw new Error('Only innovators can update subsystems');
      }
      return await apiPatch<SubsystemSelectDto>({
        path: `/v1/innovator/subsystems/${data.id}`,
        body: data,
      });
    },
    onSuccess,
  });
};

export const useDeleteTechnologySubsystem = () => {
  const { apiDelete } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: SubsystemSelectDto, variables: { id: string; technologyId: string }) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnology(variables.technologyId),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologies });
  };

  return useMutation({
    mutationFn: async ({ id }: { id: string; technologyId: string }) => {
      if (!isInnovator) {
        throw new Error('Only innovators can delete subsystems');
      }
      return await apiDelete<SubsystemSelectDto>({
        path: `/v1/innovator/subsystems/${id}`,
      });
    },
    onSuccess,
  });
};
